import app from 'app';
import 'm/RealTimePreviewModel';
import Marionette from 'backbone.marionette';
import storyViewBus from '../views/Story/storyViewBus';
import _ from 'lodash-extended';

app.namespace.Controller.RealTimePreviewsController = Marionette.Object.extend({
  rtObjectAttr: 'realTimePreviewResultData',
  rtChecksumAttr: 'realTimePreviewChecksum',
  previewModels: {},
  previewQueue: {},

  initialize: function (options) {
    this.options = $.extend(
      {
        shouldLog: !app.rootStore.getters['user/box'].isProd(),
      },
      options
    );
    // /*DBG*/ app.rtpModels = this.previewModels;
  },

  onBeforeDestroy: function () {
    this.resetCache();
  },

  resetCache: function () {
    _.each(
      this.previewModels,
      (checksum) => {
        _.each(
          checksum,
          (type) => {
            type.model.abort();
          },
          this
        );
      },
      this
    );
    this.previewModels = {};
  },

  addPreviewModel: function (options) {
    const newModel = new app.namespace.Model.RealTimePreviewModel(
      _.extend(
        {
          programmerId: app.storyController.story.get('programmerId'),
          topicId: app.storyController.story.get('topicId'),
          // static / static masks are always 16x9
          aspectRatio:
            /STATIC/.test(options.type) && !options.forceStoryAspectRatio
              ? 'ASPECT_16_9'
              : app.storyController.story.get('aspectRatio'),
          type: options.type,
        },
        options
      )
    );

    // cache model:
    // previewModels[checksum][type(static/mask/video)] = {hits: (int), model}
    if (_.isEmpty(this.previewModels[options.realTimePreviewChecksum])) {
      this.previewModels[options.realTimePreviewChecksum] = {};
    }
    this.previewModels[options.realTimePreviewChecksum][options.type] = {
      hits: 1,
      model: newModel,
    };
    this.listenTo(newModel, 'change', function (previewModel) {
      if (previewModel.get('status') === 'DONE') {
        app.trigger('app.rtp.done', previewModel);
      }
    });
    return newModel;
  },

  _proccessSingleVisualTrack: function (options) {
    options = $.extend(
      {
        visualTrack: null,
        silent: false,
        source: null,
        forceStoryAspectRatio: false,
        type: null,
      },
      options
    );

    const themeOverrides = app.rootStore.getters.themeOverrides;
    const story = app.storyController.story;
    const storyCustomOverlays =
      story &&
      story.get &&
      story
        .get('customOverlays')
        ?.filter((co) => !co.noRealtimePreview && !co.disabled);
    const newChecksum = app.generateAssetRealTimeChecksum({
      visualTrack: options.visualTrack,
      noInstantPreviewFieldsObject: options.noInstantPreviewFieldsObject,
      aspectRatio: app.storyController.story.get('aspectRatio'),
      themeOverrides,
      type: options.type,
      storyCustomOverlays,
    });
    const rtp = this.getPreviewModelByChecksum(newChecksum, options.type);
    let rtpModel = null;
    let initPreview = false;

    if (rtp) {
      rtpModel = rtp.model;
      rtpModel.set('silent', options.silent);
      this._log(newChecksum, ' - RTP exists in cache');
      this._processStatusChanged(rtpModel);
      if (rtpModel.get('status') === 'ABORT') {
        initPreview = true;
      }
    } else {
      rtpModel = this.addPreviewModel({
        realTimePreviewChecksum: newChecksum,
        themeOverrides,
        type: options.type,
        visualTrack: options.visualTrack,
        silent: options.silent,
        source: options.source,
        forceStoryAspectRatio: options.forceStoryAspectRatio,
        height: options.height,
        storyCustomOverlays,
      });
      initPreview = true;
    }

    if (initPreview) {
      rtpModel.initPreview();
      this.listenTo(rtpModel, 'change', this._processStatusChanged);
      if (!options.silent) {
        storyViewBus.$emit('svbTimelineRtp', { rtpModel, update: true });
      }
      this.listenTo(rtpModel, 'destroy', this._previewModelAborted);
    }

    return rtpModel;
  },

  increaseRtpHits: function (checksum, type) {
    const rtp = this.getPreviewModelByChecksum(checksum, type);
    if (rtp) {
      rtp.hits++;
    }
  },

  abortRTP: function (checksum, type) {
    let rtp = this.getPreviewModelByChecksum(checksum, type);
    if (rtp) {
      rtp.hits--;
      if (rtp.hits === 0 && !rtp.model.isDone()) {
        // no other asset is waiting for that rtp, abort it!
        rtp.model.abort();
        rtp = null;
        this.previewModels[checksum] = null;
      }
    }
  },

  /**
   * Forget a cached preview so the next request for the same checksum renders
   * again instead of reusing the result.
   *
   * Needed when the render inputs did not change but the data the renderer
   * pulls did - object tracking finishing is the case: the agd (and therefore
   * the checksum) is identical, so _proccessSingleVisualTrack would hand back
   * the render made while tracking was still processing.
   *
   * A forgotten render is also STOPPED, not just dropped. Forgetting one that is
   * still in flight would otherwise leave it running with nothing able to reach
   * it - `abortRTP` looks models up in this very map - and it keeps reporting
   * status. Two live renders then share a checksum, and the first to finish
   * writes its result to the track and nulls the `_tmpRealTimePreviewChecksum`
   * the timeline matches on, so the second is discarded. The stale one usually
   * wins that race, because it started first.
   *
   * `abortRTP` is not a substitute: it keeps a DONE model in place.
   *
   * @param checksum {String}
   */
  invalidatePreviewModel: function (checksum) {
    const byType = this.previewModels[checksum];

    if (!byType) {
      return;
    }

    this._log(checksum, ' - dropping cached RTP');

    const models = _.compact(_.map(byType, (entry) => entry && entry.model));

    // silence the whole set FIRST. renderAsset keys its in-flight request by
    // checksum alone, so aborting one type can abort a sibling's POST, and a
    // sibling not yet stopped would report that failure into the timeline
    _.each(models, (model) => this.stopListening(model));

    _.each(models, (model) => {
      if (!model.isDone()) {
        model.abort();
      }
    });

    this.previewModels[checksum] = null;
  },

  /**
   * Forget every cached preview whose visual track matches `predicate`.
   *
   * Lets a caller drop a render without knowing its checksum - object tracking
   * needs this because the asset it belongs to may still be open in the asset
   * editor, where the checksum lives on a copy of the asset.
   *
   * @param predicate {Function} receives the model's visualTrack
   * @return {Number} how many checksums were dropped
   */
  invalidatePreviewModelsBy: function (predicate) {
    let dropped = 0;

    _.each(this.previewModels, (byType, checksum) => {
      const matches = _.some(
        byType,
        (entry) =>
          entry && entry.model && predicate(entry.model.get('visualTrack'))
      );

      if (matches) {
        this.invalidatePreviewModel(checksum);
        dropped++;
      }
    });

    return dropped;
  },

  getPreviewModelByChecksum: function (checksum, type) {
    let foundModel = false;

    if (!_.isEmpty(this.previewModels[checksum])) {
      if (type && !/VIDEO/.test(type)) {
        // asked for a specific type - static / static masks
        if (this.previewModels[checksum][type]) {
          foundModel = this.previewModels[checksum][type];
        }
      } else {
        // video request should return VIDEO or VIDEO_PARTS
        if (this.previewModels[checksum][app.RealtimePreviewType.VIDEO_PARTS]) {
          foundModel =
            this.previewModels[checksum][app.RealtimePreviewType.VIDEO_PARTS];
        }
        if (this.previewModels[checksum][app.RealtimePreviewType.VIDEO]) {
          foundModel =
            this.previewModels[checksum][app.RealtimePreviewType.VIDEO];
        }
      }
    }

    return foundModel.model ? foundModel : false;
  },

  /**
   * check if timeline visual track has rtp in progress
   * @note: assumes comparison by guid
   * @param asset {Object}
   * @param rtpType {app.RealtimePreviewType}
   */
  isInProgress: function (asset, rtpType) {
    let found = false;
    _.each(this.previewModels, (allTypes, checksum) => {
      if (rtpType && allTypes && allTypes[rtpType] && allTypes[rtpType].model) {
        const model = allTypes[rtpType].model;
        const visualTrack = model.get('visualTrack');
        if (visualTrack.guid === asset.guid && model.get('status') !== 'DONE') {
          found = true;
          return false; // break;
        }
      }
    });
    return found;
  },

  addTrackToPreviewQueue: function (options) {
    options = $.extend(
      {
        delay: app.TIME.SECOND,
        visualTrack: {},
        silent: false,
        type: app.RealtimePreviewType.VIDEO,
        source: null,
      },
      options
    );

    const deferred = new $.Deferred();
    app.controller.storyPromise.done(() => {
      if (this.shouldPullPreview(options.visualTrack, options)) {
        this.removeTrackFromPreviewQueue(options.visualTrack);
        if (options.delay) {
          this.previewQueue[options.visualTrack.guid] = window.setTimeout(
            () => {
              deferred.resolve(this._proccessSingleVisualTrack(options));
              delete this.previewQueue[options.visualTrack.guid];
            },
            options.delay
          );
        } else {
          deferred.resolve(this._proccessSingleVisualTrack(options));
        }
      } else {
        deferred.reject();
      }
    });
    return deferred.promise();
  },

  removeTrackFromPreviewQueue: function (visualTrack) {
    if (this.previewQueue[visualTrack.guid]) {
      window.clearTimeout(this.previewQueue[visualTrack.guid]);
      delete this.previewQueue[visualTrack.guid];
    }
    // if (visualTrack._tmpRealTimePreviewChecksum
    //     && !_.isEmpty(this.previewModels[visualTrack._tmpRealTimePreviewChecksum])){
    //         _.each(this.previewModels[visualTrack._tmpRealTimePreviewChecksum], (rtpModel)=>{
    //             rtpModel.abort();
    //         });
    // }
  },

  _processStatusChanged: function (rtpModel) {
    if (!rtpModel.get('silent')) {
      storyViewBus.$emit('svbTimelineRtp', { rtpModel, update: false });
    }
  },

  _previewModelAborted: function (previewModel) {
    if (
      _.isArray(this.previewModels[previewModel.get('realTimePreviewChecksum')])
    ) {
      this.previewModels[previewModel.get('realTimePreviewChecksum')][
        previewModel.get('type')
      ] = null;
    }
  },

  shouldPullPreview: function (visualTrack, options) {
    options = _.options(options, {
      type: null,
    });
    const assetModel = new app.namespace.Model.AssetModel(visualTrack);
    const story = app.storyController.story;
    const storyCustomOverlays =
      story &&
      story.get &&
      story
        .get('customOverlays')
        ?.filter((co) => !co.noRealtimePreview && !co.disabled);
    return (
      assetModel.isINFOGRX() ||
      assetModel.isSOCIAL() ||
      assetModel.isGIF() ||
      assetModel.isSOCIALFRAGMENT() ||
      assetModel.isOverlayTracks() ||
      storyCustomOverlays?.length ||
      this.agdRequiresRtp(assetModel, options.type)
    );
  },

  agdRequiresRtp: function (asset, rtpType) {
    asset = asset.attributes ? asset.attributes : asset;
    rtpType = rtpType ? rtpType : app.RealtimePreviewType.VIDEO;
    const agd = asset.agd;
    const comparators = [
      (val, key) => app.CONDITIONAL_REALTIME_IGNORE_FIELDS.indexOf(key) === -1,
      (val, key) => app.REALTIME_IGNORE_FIELDS.indexOf(key) === -1,
      (val, key) =>
        key === 'videoSpeed' ? _.isNumber(val) && val !== 1 : !_.isEmpty(val),
      (val, key) => {
        const forMask = rtpType === app.RealtimePreviewType.STATIC_MASK;
        if (!forMask && key === 'customOverlays') {
          const filtered = _.filter(val, (co) => !co.noRealtimePreview);
          return filtered.length > 0;
        } else {
          return true;
        }
      },
    ];

    const agdArr = _.filter(agd, (val, key) => {
      return _.every(comparators.map((fn) => fn(val, key)));
    });

    return agdArr.length > 0;
  },

  _log: function (...args) {
    if (this.options.shouldLog) {
      console.log('%cRTPC::', 'color: #AB00B3', ...args);
    }
  },
});
